import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import {
  Loader2, BadgePercent, Sparkles, Wallet, Link2, TrendingUp, ArrowRight, Building2, User, Mail, Phone, Lock,
} from "lucide-react";

const BENEFITS = [
  { icon: BadgePercent, title: "Earn on every referral", desc: "Get a commission for every traveler you refer who books a tour — plus extra 'Bonus Boost' rewards on select experiences." },
  { icon: Link2, title: "Your own tracking links", desc: "Generate a unique link for any tour and share it with your guests, followers or clients. We track every sale automatically." },
  { icon: Wallet, title: "Simple payouts", desc: "Watch your balance grow in a live earnings ledger and request a payout once you hit the $50 threshold." },
  { icon: TrendingUp, title: "Perfect for hotels & influencers", desc: "Ideal for hotels, villas, concierges, guides and content creators who already send travelers our way." },
];

export default function BecomeAnAgent() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        role: "agent",
        business_name: form.business_name,
        phone: form.phone,
      });
      toast.success("Application submitted! Your agent account is pending admin approval.");
      navigate("/agent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl pl-11 pr-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50 placeholder:text-slate-600";

  return (
    <div className="min-h-screen bg-[#060810]">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#1D2640]">
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600/15 via-transparent to-cyan-500/15" />
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 font-mono-caption text-xs uppercase tracking-[0.2em] text-fuchsia-400 mb-4">
              <Sparkles className="w-4 h-4" /> Carivisor Affiliate Program
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]">
              Earn bonuses for every traveler you refer
            </h1>
            <p className="text-slate-300 text-base mt-5 max-w-xl leading-relaxed">
              Turn your audience, guests or network into income. Share unique links to the Caribbean's best tours and get paid for every booking — no inventory, no hassle.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a href="#apply" data-testid="hero-apply-cta" className="btn-neon rounded-full px-7 py-3 font-semibold text-sm inline-flex items-center gap-2">
                Become an Agent <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/login" className="text-sm text-slate-300 hover:text-cyan-400">Already an agent? Sign in</Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="glass rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-grad-cool flex items-center justify-center text-white mb-3">
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-white text-sm">{b.title}</h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application form */}
      <div id="apply" className="px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto py-16">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Apply to become an agent</h2>
          <p className="text-slate-400 text-sm mt-2">Tell us a bit about you. We'll review your application and get you set up.</p>
        </div>

        <form onSubmit={submit} className="glass neon-border rounded-2xl p-6 sm:p-8 space-y-5" data-testid="agent-apply-form">
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Full name</label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input required data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Sarah Charles" className={inputCls} />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Business name</label>
            <div className="relative">
              <Building2 className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input data-testid="apply-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Grand Anse Hotel" className={inputCls} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input required type="email" data-testid="apply-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Phone number</label>
              <div className="relative">
                <Phone className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 473 555 0123" className={inputCls} />
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Create a password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input required type="password" minLength={6} data-testid="apply-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" className={inputCls} />
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">You'll use this to sign in once your application is approved.</p>
          </div>

          <button type="submit" disabled={loading} data-testid="apply-submit" className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgePercent className="w-4 h-4" />} Submit application
          </button>
          <p className="text-center text-[11px] text-slate-500">
            By applying you agree to our <Link to="/terms" className="text-cyan-400 hover:underline">Terms</Link>. Applications are reviewed by our team before activation.
          </p>
        </form>
      </div>
      <Footer />
    </div>
  );
}
